import { useAuth, useClerk, useOrganizationList } from "@clerk/clerk-react";
import { useSubscription } from "@clerk/clerk-react/experimental";
import type { BillingSubscriptionResource } from "@clerk/types";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { FREE_PLAN_SLUGS } from "./BillingStatusContext";

function isPaidSubscription(
  subscription: BillingSubscriptionResource | null | undefined,
): boolean {
  if (!subscription || subscription.status !== "active") return false;
  return subscription.subscriptionItems.some(
    (item) => item.plan.hasBaseFee === true,
  );
}

interface SwitchParams {
  clerk: ReturnType<typeof useClerk>;
  hasAttempted: React.MutableRefObject<boolean>;
}

async function checkPersonalSubscription(
  subscription: BillingSubscriptionResource | null | undefined,
  { clerk, hasAttempted }: SwitchParams,
): Promise<boolean> {
  if (!isPaidSubscription(subscription)) return false;
  hasAttempted.current = true;
  try {
    await clerk.setActive({ organization: null });
  } catch {
    hasAttempted.current = false;
  }
  return true;
}

type OrgMembership = NonNullable<
  ReturnType<typeof useOrganizationList>["userMemberships"]["data"]
>[number];

export function isExemptOrg(membership: OrgMembership): boolean {
  return membership.organization.publicMetadata?.billing_exempt === true;
}

/**
 * Switch to a free org that's been flagged billing_exempt in its Clerk public
 * metadata. Prioritized over paid orgs so internal/demo orgs win, matching the
 * requirement that the metadata-flagged org is preferred.
 */
async function findAndSwitchToExemptOrg(
  orgs: OrgMembership[],
  { clerk, hasAttempted }: SwitchParams,
): Promise<boolean> {
  const exemptOrg = orgs.find(isExemptOrg);
  if (!exemptOrg) return false;
  hasAttempted.current = true;
  try {
    await clerk.setActive({ organization: exemptOrg.organization });
  } catch {
    hasAttempted.current = false;
    return false;
  }
  return true;
}

async function findAndSwitchToPaidOrg(
  orgs: OrgMembership[],
  { clerk, hasAttempted }: SwitchParams,
): Promise<boolean> {
  for (const membership of orgs) {
    const orgId = membership.organization.id;
    try {
      const orgSubscription = await clerk.billing.getSubscription({ orgId });
      if (isPaidSubscription(orgSubscription)) {
        hasAttempted.current = true;
        await clerk.setActive({ organization: membership.organization });
        return true;
      }
    } catch {
      // No subscription for this org, continue
    }
  }
  return false;
}

async function handlePricingPageFallback(
  orgs: OrgMembership[],
  pathname: string | undefined,
  { clerk, hasAttempted }: SwitchParams,
): Promise<void> {
  if (pathname?.startsWith("/pricing") && !hasAttempted.current) {
    hasAttempted.current = true;
    await clerk.setActive({ organization: orgs[0].organization });
  }
}

/**
 * Automatically switches to a paid org context when:
 * 1. User is on a free plan
 * 2. User has a paid personal subscription → switch to personal
 * 3. User belongs to a paid org → switch to first paid org
 * 4. On pricing page with orgs → switch to first org for display
 */
export function BillingContextSwitcher() {
  const { isSignedIn, sessionClaims } = useAuth();
  const clerk = useClerk();
  const { data: subscription } = useSubscription();
  const { userMemberships, isLoaded: isOrgsLoaded } = useOrganizationList({
    userMemberships: { pageSize: 50 },
  });
  const router = useRouterState();
  const hasAttempted = useRef(false);

  useEffect(() => {
    if (!isSignedIn) hasAttempted.current = false;
  }, [isSignedIn]);

  useEffect(() => {
    if (!(isSignedIn && isOrgsLoaded && clerk.loaded) || hasAttempted.current)
      return;

    const activePlan = sessionClaims?.pla;
    const isOnFreePlan =
      typeof activePlan === "string" && FREE_PLAN_SLUGS.has(activePlan);
    if (!isOnFreePlan) return;

    const params = { clerk, hasAttempted };
    const orgs = userMemberships?.data || [];
    const pathname = router.location?.pathname;

    async function attemptSwitch() {
      if (await checkPersonalSubscription(subscription, params)) return;
      if (orgs.length === 0) return;
      try {
        if (await findAndSwitchToExemptOrg(orgs, params)) return;
        if (await findAndSwitchToPaidOrg(orgs, params)) return;
        await handlePricingPageFallback(orgs, pathname, params);
      } catch (err) {
        console.error("Error during billing context switch:", err);
      }
    }

    attemptSwitch();
  }, [
    isSignedIn,
    isOrgsLoaded,
    clerk,
    sessionClaims,
    subscription,
    userMemberships,
    router.location?.pathname,
  ]);

  return null;
}
